import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UseFormRegisterReturn, UseFormTrigger } from "react-hook-form";
import MonthSelectField from "components/form-fields/month-select-field/MonthSelectField";
import DayInputField from "components/form-fields/day-input-field/DayInputField";
import YearInputField from "components/form-fields/year-input-field/YearInputField";

interface DateOfBirthFieldProps {
    monthRegister: UseFormRegisterReturn;
    dayRegister: UseFormRegisterReturn;
    yearRegister: UseFormRegisterReturn;
    monthValue?: string;
    dayValue?: string;
    yearValue?: string;
    monthError?: string;
    dayError?: string;
    yearError?: string;
    isEditable?: boolean;
    disabled?: boolean;
    id?: string;
    onBlur?: () => void;
    /**
     * RHF trigger. When provided, the whole date-of-birth group is validated on blur
     * once all three fields are filled — so callers don't repeat the trigger wiring.
     */
    trigger?: UseFormTrigger<any>;
    editNavigationPath?: string;
    /** When provided, called on edit click instead of navigating to editNavigationPath */
    onEditClick?: () => void;
}

/**
 * Filters input to numeric characters and enforces max length
 */
const filterNumericInput = (value: string, maxLength: number): string => {
    const numericValue = value.replace(/\D/g, "");
    return numericValue.slice(0, maxLength);
};

/**
 * Date of birth field with month, day, and year inputs
 */
const DateOfBirthField = ({
    monthRegister,
    dayRegister,
    yearRegister,
    monthValue,
    dayValue,
    yearValue,
    monthError,
    dayError,
    yearError,
    isEditable = false,
    disabled = false,
    id,
    onBlur,
    trigger,
    editNavigationPath,
    onEditClick,
}: DateOfBirthFieldProps) => {
    // ========== Hooks ==========
    const navigate = useNavigate();

    // ========== Event Handlers ==========
    const handleEditClick = () => {
        if (onEditClick) {
            onEditClick();
        } else if (editNavigationPath) {
            navigate(editNavigationPath);
            setTimeout(() => {
                document.getElementById("hero-card")?.scrollIntoView({ behavior: "auto" });
            }, 0);
        }
    };

    // Validate / notify only once all three fields are filled and the user leaves the group
    const handleBlur = useCallback(() => {
        if (!monthValue || !dayValue || !yearValue) return;
        onBlur?.();
        if (trigger) {
            // Derive the date-of-birth group path (e.g. "quote.dateOfBirth") from the month field name
            void trigger(monthRegister.name.replace(/\.month$/, ""));
        }
    }, [onBlur, trigger, monthRegister.name, monthValue, dayValue, yearValue]);

    const handleMonthChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            monthRegister.onChange?.(e);
            monthRegister.onBlur?.({ target: { name: monthRegister.name, value: e.target.value } } as any);
            handleBlur();
        },
        [monthRegister, handleBlur]
    );

    const handleDayChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            e.target.value = filterNumericInput(e.target.value, 2);
            dayRegister.onChange?.(e);
        },
        [dayRegister]
    );

    const handleYearChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            e.target.value = filterNumericInput(e.target.value, 4);
            yearRegister.onChange?.(e);
        },
        [yearRegister]
    );

    // ========== Computed Values ==========
    const isFieldDisabled = disabled || isEditable;

    // ========== Render ==========
    return (
        <div className="form-control">
            {isEditable ? (
                <div className="flex justify-between items-center">
                    <div className="label h-7">
                        <span className="text-base-content">Date of Birth</span>
                    </div>
                    <button onClick={handleEditClick} className="text-edit-link font-bold text-sm leading-5 hover:underline">
                        Edit
                    </button>
                </div>
            ) : (
                <div className="label h-7">
                    <span className="text-base-content">Date of Birth</span>
                </div>
            )}
            <div>
                <div className="flex gap-2">
                    <MonthSelectField
                        register={monthRegister}
                        value={monthValue}
                        error={monthError}
                        disabled={isFieldDisabled}
                        id={id ? `${id}-month` : undefined}
                        onChange={handleMonthChange}
                    />
                    <DayInputField
                        register={dayRegister}
                        value={dayValue}
                        error={dayError}
                        disabled={isFieldDisabled}
                        id={id ? `${id}-day` : undefined}
                        onChange={handleDayChange}
                        onBlur={handleBlur}
                    />
                    <YearInputField
                        register={yearRegister}
                        value={yearValue}
                        error={yearError}
                        disabled={isFieldDisabled}
                        id={id ? `${id}-year` : undefined}
                        onChange={handleYearChange}
                        onBlur={handleBlur}
                    />
                </div>
                {(monthError || dayError || yearError) && (
                    <div className="text-xs mt-1">
                        <p className="text-error">{monthError || dayError || yearError}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DateOfBirthField;
